import type { components } from '@/generated/core-api'
import { API_ROOT, request, requestRaw } from '@/shared/api/transport'
import { CoreError } from '@/shared/api/errors'
import type { ModulePatientAlerts } from '@/shared/externalModules/types'

/**
 * Smartomica's OWN wire reads and commands — the module's whole HTTP surface
 * in one place, typed from the generated contract and confined to the vendor
 * module the way the composition rule demands.
 *
 * EVERY READ HERE IS A STORED READ on the server, and every command is
 * refused by the server's module lifecycle before it acts — the browser's
 * lifecycle gates decide only what to OFFER. Nothing in this file polls: the
 * change poller below answers ONE frame per request, and its caller owns the
 * cadence and stops it whenever the module is not enabled.
 */

export type SmartomicaFlowPage = components['schemas']['SmartomicaFlowPage']
export type SmartomicaFlowItem = components['schemas']['SmartomicaFlowItem']
export type SmartomicaFlowFacets = components['schemas']['SmartomicaFlowFacets']
export type SmartomicaPatientEvent = components['schemas']['SmartomicaPatientEvent']
export type SmartomicaPatientEventMessage = components['schemas']['SmartomicaPatientEventMessage']
/**
 * THE CLINICAL SUMMARY the source sends with an event: the actual PDF bytes,
 * base64 inside the event JSON, at most 128 KiB decoded. The server stores
 * and returns it on the authorized event read exactly as it arrived and never
 * fetches a vendor URL for it.
 */
export type SmartomicaClinicalSummary = components['schemas']['SmartomicaClinicalSummary']
export type SmartomicaEvent = components['schemas']['SmartomicaEvent']
export type SmartomicaEventTransition = components['schemas']['SmartomicaEventTransition']
export type SmartomicaObservation = components['schemas']['SmartomicaObservation']
export type SmartomicaDraft = components['schemas']['SmartomicaDraft']
export type SmartomicaDraftItem = components['schemas']['SmartomicaDraftItem']
export type SmartomicaReview = components['schemas']['SmartomicaReview']
export type SmartomicaMessageEvidence = components['schemas']['SmartomicaMessageEvidence']
export type SmartomicaApprovalResponse = components['schemas']['SmartomicaApprovalResponse']
export type SmartomicaDashboard = components['schemas']['SmartomicaDashboard']
export type SmartomicaChangePage = components['schemas']['SmartomicaChangePage']
export type SmartomicaCandidate = components['schemas']['SmartomicaCandidate']
export type SmartomicaCandidateMatch = components['schemas']['SmartomicaCandidateMatch']
export type SmartomicaCandidateListItem = components['schemas']['SmartomicaCandidateListItem']
export type SmartomicaReconcileResponse = components['schemas']['SmartomicaReconcileResponse']

export async function resolveSmartomicaNotificationTarget(eventId: string, signal?: AbortSignal) {
  const body = await request('GET', `${API_ROOT}/account/smartomica-events/${encodeURIComponent(eventId)}/target`, signal ? { signal } : {}) as
    components['schemas']['SmartomicaNotificationTarget'] | undefined
  if (!body || body.eventId !== eventId || typeof body.clinicId !== 'string' || !body.clinicId) {
    throw new CoreError('malformed', 'Notification target is invalid')
  }
  return { ...body, moduleCode: 'smartomica' }
}

export interface SmartomicaFlowQuery {
  cursor?: string
  priority?: 'critical' | 'review'
  /**
   * The lifecycle set the page answers with, as the REPEATED `status`
   * parameter. An omitted set is the server's standing default — every status
   * except `closed` — so a caller that wants the whole ledger names all six
   * rather than sending nothing.
   */
  statuses?: readonly SmartomicaFlowItem['lifecycleStatus'][]
  assignedPractitionerId?: string
  receivedFrom?: string
  receivedTo?: string
  q?: string
  limit?: number
}

export async function listSmartomicaFlow(
  query: SmartomicaFlowQuery,
  signal?: AbortSignal,
): Promise<SmartomicaFlowPage> {
  const { statuses, ...scalars } = query
  const parameters: Record<string, string | readonly string[]> = {}
  for (const [key, value] of Object.entries(scalars)) {
    if (value !== undefined && value !== '') {
      parameters[key] = String(value)
    }
  }
  if (statuses !== undefined && statuses.length > 0) {
    parameters.status = [...statuses]
  }
  return (await request('GET', `${API_ROOT}/smartomica/flow`, {
    query: parameters,
    ...(signal ? { signal } : {}),
  })) as SmartomicaFlowPage
}

export async function getSmartomicaEvent(
  eventId: string,
  signal?: AbortSignal,
): Promise<SmartomicaEvent> {
  return (await request('GET', `${API_ROOT}/smartomica/events/${encodeURIComponent(eventId)}`, {
    ...(signal ? { signal } : {}),
  })) as SmartomicaEvent
}

/** The EFFECTFUL open: records the reviewing transition. Never issued by a
 *  render — only the workspace's explicit open action calls it. */
export async function openSmartomicaEvent(
  eventId: string,
  input: { expectedRevision: number; reason: string },
): Promise<SmartomicaEvent> {
  return (await request(
    'POST',
    `${API_ROOT}/smartomica/events/${encodeURIComponent(eventId)}/open`,
    { body: input },
  )) as SmartomicaEvent
}

export async function generateSmartomicaDraft(
  eventId: string,
  input: components['schemas']['SmartomicaAIRequest'],
): Promise<SmartomicaDraft> {
  return (await request(
    'POST',
    `${API_ROOT}/smartomica/events/${encodeURIComponent(eventId)}/ai-draft`,
    { body: input },
  )) as SmartomicaDraft
}

export async function saveSmartomicaDraft(
  eventId: string,
  input: components['schemas']['SmartomicaDraftRequest'],
): Promise<SmartomicaDraft> {
  return (await request(
    'PUT',
    `${API_ROOT}/smartomica/events/${encodeURIComponent(eventId)}/draft`,
    { body: input },
  )) as SmartomicaDraft
}

/**
 * The attested approval: Review, timeline fact and pending Patient message in
 * one atomic commit. The Idempotency-Key identifies THIS submission intent —
 * a retry after an unclear outcome reuses the same key, a new decision mints
 * a new one.
 */
export async function approveSmartomicaEvent(
  eventId: string,
  input: components['schemas']['SmartomicaApprovalRequest'],
  idempotencyKey: string,
): Promise<SmartomicaApprovalResponse> {
  return (await request(
    'POST',
    `${API_ROOT}/smartomica/events/${encodeURIComponent(eventId)}/approval`,
    { body: input, idempotencyKey: idempotencyKey },
  )) as SmartomicaApprovalResponse
}

/** Retry an approved recommendation's send along the same message identity. */
export async function sendSmartomicaRecommendation(
  eventId: string,
): Promise<SmartomicaMessageEvidence> {
  return (await request(
    'POST',
    `${API_ROOT}/smartomica/events/${encodeURIComponent(eventId)}/send`,
    {},
  )) as SmartomicaMessageEvidence
}

/**
 * THE INCOMING PATIENTS: every unresolved candidate — an external identity
 * the module received and could not resolve by exact value — with the Core
 * Patient owner's possible-duplicate suggestions beside it. A stored read
 * behind the reconcile capability; the suggestions are computed on the server
 * after the module read and the browser only displays them. Empty
 * `possibleMatches` is the server's own "no chart resembles this person".
 */
export async function listSmartomicaCandidates(
  signal?: AbortSignal,
): Promise<readonly SmartomicaCandidateListItem[]> {
  const body = (await request('GET', `${API_ROOT}/smartomica/candidates`, {
    ...(signal ? { signal } : {}),
  })) as components['schemas']['SmartomicaCandidateList']
  return body.items
}

/**
 * THE EXPLICIT RECONCILIATION: link an unresolved identity to ONE Patient the
 * clinician chose, under a reason. The server activates the identity link,
 * marks the candidate linked and releases every held ingress for it — so
 * the candidate leaves the incoming list by its own status, never by a
 * browser-side removal. `expectedRevision` is the candidate's revision the
 * decision was made against; a stale one is refused, not merged.
 */
export async function reconcileSmartomicaCandidate(
  candidateId: string,
  input: components['schemas']['SmartomicaReconcileRequest'],
): Promise<SmartomicaReconcileResponse> {
  return (await request(
    'POST',
    `${API_ROOT}/smartomica/candidates/${encodeURIComponent(candidateId)}/reconcile`,
    { body: input },
  )) as SmartomicaReconcileResponse
}

export async function getSmartomicaDashboard(signal?: AbortSignal): Promise<SmartomicaDashboard> {
  return (await request('GET', `${API_ROOT}/smartomica/dashboard`, {
    ...(signal ? { signal } : {}),
  })) as SmartomicaDashboard
}

export async function listPatientSmartomicaEvents(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly SmartomicaPatientEvent[]> {
  const body = (await request(
    'GET',
    `${API_ROOT}/smartomica/patients/${encodeURIComponent(patientId)}`,
    { ...(signal ? { signal } : {}) },
  )) as components['schemas']['SmartomicaPatientEvents']
  return body.items
}

/**
 * One registry page's open-alert counters in ONE bounded request — the same
 * set-read contract as the external-modules batch, zeros included so a
 * Patient with no open alerts is a truthful zero rather than a missing row.
 */
export async function batchSmartomicaPatientAlerts(
  patientIds: readonly string[],
  signal?: AbortSignal,
): Promise<ReadonlyMap<string, ModulePatientAlerts>> {
  const body = (await request('POST', `${API_ROOT}/smartomica/patient-alerts:batch`, {
    body: { patientIds: [...patientIds] },
    ...(signal ? { signal } : {}),
  })) as components['schemas']['SmartomicaPatientAlertsBatchResponse']
  return new Map(
    body.items.map((item) => [
      item.patientId,
      {
        critical: item.critical,
        review: item.review,
        open: item.open,
        criticalEventId: item.criticalEventId,
        openEventId: item.openEventId,
      },
    ]),
  )
}

/**
 * ONE invalidation frame from the change stream.
 *
 * The server writes exactly one SSE frame per request and ends the response —
 * `event: invalidation|reset`, `id: <cursor>`, `data: <SmartomicaChangePage>`
 * — so the browser's poller is an ordinary bounded fetch, not a held
 * connection. The parse reads the frame's `data:` payload; a body that does
 * not carry one is a malformed answer, never silently treated as quiet.
 */
export async function fetchSmartomicaChanges(
  cursor: number,
  signal?: AbortSignal,
): Promise<SmartomicaChangePage> {
  const response = await requestRaw('GET', `${API_ROOT}/smartomica/changes`, {
    query: { cursor: String(cursor) },
    ...(signal ? { signal } : {}),
  })
  if (response === null) {
    throw new CoreError('malformed', 'smartomica change stream answered no body')
  }
  const text = await response.text()
  const data = text
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).trim())
    .join('')
  if (data === '') {
    throw new CoreError('malformed', 'smartomica change frame carried no data')
  }
  try {
    return JSON.parse(data) as SmartomicaChangePage
  } catch {
    throw new CoreError('malformed', 'smartomica change frame was not JSON')
  }
}

export async function discardSmartomicaCandidate(
  candidateId: string,
  input: components['schemas']['SmartomicaDiscardRequest'],
): Promise<SmartomicaCandidate> {
  return await request('POST', `${API_ROOT}/smartomica/candidates/${encodeURIComponent(candidateId)}/discard`, {
    body: input,
  }) as SmartomicaCandidate
}

export type SmartomicaQuestionTurn = components['schemas']['SmartomicaQuestionTurn']
export type SmartomicaQuestionRequest = components['schemas']['SmartomicaQuestionRequest']
export async function getSmartomicaQuestions(eventId: string, signal?: AbortSignal): Promise<components['schemas']['SmartomicaQuestionHistory']> {
  return await request('GET', `${API_ROOT}/smartomica/events/${encodeURIComponent(eventId)}/questions`, { ...(signal ? { signal } : {}) }) as components['schemas']['SmartomicaQuestionHistory']
}
export async function askSmartomicaQuestion(eventId: string, body: SmartomicaQuestionRequest): Promise<SmartomicaQuestionTurn> {
  return await request('POST', `${API_ROOT}/smartomica/events/${encodeURIComponent(eventId)}/questions`, { body }) as SmartomicaQuestionTurn
}
